import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PurchaseOrdersService, PurchaseOrder, PurchaseOrderItem } from '../../services/purchase-orders.service';

export interface PurchaseOrderDialogData {
  title: string;
  purchaseOrder?: PurchaseOrder;
}

@Component({
  selector: 'app-purchase-order-dialog',
  templateUrl: './purchase-order-dialog.component.html',
  styleUrls: ['./purchase-order-dialog.component.css']
})
export class PurchaseOrderDialogComponent implements OnInit {
  purchaseOrderForm: FormGroup;
  isLoading = false;
  isEdit = false;
  orderStatuses = ['Pending', 'Processing', 'Shipped', 'Delivered', 'Cancelled'];
  suppliers = [
    { id: 1, name: 'ABC Pipes Ltd' },
    { id: 2, name: 'XYZ Supplies' },
    { id: 3, name: 'Global Pipe Co' },
    { id: 4, name: 'Regional Materials' }
  ];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<PurchaseOrderDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PurchaseOrderDialogData,
    private purchaseOrdersService: PurchaseOrdersService,
    private snackBar: MatSnackBar
  ) {
    this.purchaseOrderForm = this.createForm();
    this.isEdit = !!data.purchaseOrder;
  }

  ngOnInit(): void {
    if (this.data.purchaseOrder) {
      this.purchaseOrderForm.patchValue(this.data.purchaseOrder);
      // Load items if they exist
      if (this.data.purchaseOrder.items) {
        this.data.purchaseOrder.items.forEach(item => {
          this.items.push(this.createItemFormGroup(item));
        });
      }
    }
    
    // Update totals when items change
    this.items.valueChanges.subscribe(() => this.updateTotals());
  }

  createForm(): FormGroup {
    return this.fb.group({
      id: [null],
      orderNumber: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20)]],
      supplierName: ['', [Validators.required]],
      supplierId: [null],
      orderDate: [new Date().toISOString().split('T')[0], [Validators.required]],
      expectedDeliveryDate: ['', [Validators.required]],
      actualDeliveryDate: [''],
      status: ['Pending', [Validators.required]],
      createdBy: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      approvedBy: [''],
      approvedAt: [''],
      notes: ['', [Validators.maxLength(500)]],
      items: this.fb.array([])
    });
  }

  get items(): FormArray {
    return this.purchaseOrderForm.get('items') as FormArray;
  }

  createItemFormGroup(item?: PurchaseOrderItem): FormGroup {
    return this.fb.group({
      id: [item?.id || null],
      productCode: [item?.productCode || '', [Validators.required, Validators.minLength(2), Validators.maxLength(20)]],
      productName: [item?.productName || '', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      quantity: [item?.quantity || 1, [Validators.required, Validators.min(1), Validators.max(10000)]],
      unitPrice: [item?.unitPrice || 0, [Validators.required, Validators.min(0.01), Validators.max(99999.99)]],
      totalPrice: [item?.totalPrice || 0],
      specifications: [item?.specifications || '', [Validators.maxLength(200)]],
      notes: [item?.notes || '', [Validators.maxLength(300)]]
    });
  }

  addItem(): void {
    this.items.push(this.createItemFormGroup());
  }

  removeItem(index: number): void {
    this.items.removeAt(index);
    this.updateTotals();
  }

  updateItemTotal(index: number): void {
    const itemGroup = this.items.at(index);
    const quantity = itemGroup.get('quantity')?.value || 0;
    const unitPrice = itemGroup.get('unitPrice')?.value || 0;
    const totalPrice = quantity * unitPrice;
    itemGroup.patchValue({ totalPrice }, { emitEvent: false });
  }

  updateTotals(): void {
    let totalAmount = 0;
    let itemCount = 0;

    this.items.controls.forEach((itemGroup, index) => {
      const quantity = itemGroup.get('quantity')?.value || 0;
      const unitPrice = itemGroup.get('unitPrice')?.value || 0;
      const totalPrice = quantity * unitPrice;
      
      itemGroup.patchValue({ totalPrice }, { emitEvent: false });
      totalAmount += totalPrice;
      itemCount += quantity;
    });

    this.purchaseOrderForm.patchValue({
      totalAmount: totalAmount,
      itemCount: itemCount
    }, { emitEvent: false });
  }

  get orderNumber() { return this.purchaseOrderForm.get('orderNumber'); }
  get supplierName() { return this.purchaseOrderForm.get('supplierName'); }
  get orderDate() { return this.purchaseOrderForm.get('orderDate'); }
  get expectedDeliveryDate() { return this.purchaseOrderForm.get('expectedDeliveryDate'); }
  get status() { return this.purchaseOrderForm.get('status'); }
  get createdBy() { return this.purchaseOrderForm.get('createdBy'); }

  onSubmit(): void {
    if (this.purchaseOrderForm.invalid) {
      this.markFormGroupTouched(this.purchaseOrderForm);
      this.snackBar.open('Please fill in all required fields correctly', 'Close', { duration: 3000 });
      return;
    }

    if (this.items.length === 0) {
      this.snackBar.open('Please add at least one item to the purchase order', 'Close', { duration: 3000 });
      return;
    }

    this.isLoading = false;
    const purchaseOrder: PurchaseOrder = {
      ...this.purchaseOrderForm.value,
      totalAmount: this.calculateTotalAmount(),
      itemCount: this.calculateItemCount()
    };

    if (this.isEdit) {
      this.updatePurchaseOrder(purchaseOrder);
    } else {
      this.createPurchaseOrder(purchaseOrder);
    }
  }

  createPurchaseOrder(purchaseOrder: PurchaseOrder): void {
    this.purchaseOrdersService.createPurchaseOrder(purchaseOrder).subscribe({
      next: (response) => {
        if (response.success) {
          this.snackBar.open('Purchase order created successfully', 'Close', { duration: 3000 });
          this.dialogRef.close(response.data);
        } else {
          this.snackBar.open(response.message || 'Failed to create purchase order', 'Close', { duration: 3000 });
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error creating purchase order:', error);
        this.snackBar.open('Error creating purchase order', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  updatePurchaseOrder(purchaseOrder: PurchaseOrder): void {
    if (!purchaseOrder.id) return;

    this.purchaseOrdersService.updatePurchaseOrder(purchaseOrder.id, purchaseOrder).subscribe({
      next: (response) => {
        if (response.success) {
          this.snackBar.open('Purchase order updated successfully', 'Close', { duration: 3000 });
          this.dialogRef.close(response.data);
        } else {
          this.snackBar.open(response.message || 'Failed to update purchase order', 'Close', { duration: 3000 });
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error updating purchase order:', error);
        this.snackBar.open('Error updating purchase order', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  calculateTotalAmount(): number {
    return this.items.controls.reduce((total, itemGroup) => {
      const quantity = itemGroup.get('quantity')?.value || 0;
      const unitPrice = itemGroup.get('unitPrice')?.value || 0;
      return total + (quantity * unitPrice);
    }, 0);
  }

  calculateItemCount(): number {
    return this.items.controls.reduce((total, itemGroup) => {
      return total + (itemGroup.get('quantity')?.value || 0);
    }, 0);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getErrorMessage(field: any): string {
    if (field?.errors) {
      if (field.errors['required']) {
        return 'This field is required';
      }
      if (field.errors['minlength']) {
        return `Minimum length is ${field.errors['minlength'].requiredLength} characters`;
      }
      if (field.errors['maxlength']) {
        return `Maximum length is ${field.errors['maxlength'].requiredLength} characters`;
      }
      if (field.errors['min']) {
        return `Minimum value is ${field.errors['min'].min}`;
      }
      if (field.errors['max']) {
        return `Maximum value is ${field.errors['max'].max}`;
      }
    }
    return '';
  }

  getItemErrorMessage(itemGroup: FormGroup, fieldName: string): string {
    const field = itemGroup.get(fieldName);
    return this.getErrorMessage(field);
  }
}
